import React, { useRef, useState } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import { FaArrowRight, FaBuilding, FaCheckCircle, FaHandshake, FaAward } from "react-icons/fa";
import techTraining1 from "../../assets/gallery/education/tech-training1.jpg";
import groupEventAsset from "../../assets/gallery/events/groupix.png";
import { donorRequestAPI } from "../../services/api";
import toast from "react-hot-toast";
import { validateForm as validateFormUtil } from "../../utils/validation";

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  if (isInView) {
    controls.start("visible");
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function BeACorporateDonor() {
  const groupEvent = `${groupEventAsset}?v=20260424`;
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    organization_name: "",
    organization_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const rules = {
      full_name: { required: true, label: 'Full Name' },
      email: { required: true, type: 'email', label: 'Email' },
      phone: { required: true, type: 'phone', label: 'Phone Number' },
      organization_name: { required: true, label: 'Organization Name' },
    };
    const newErrors = validateFormUtil(formData, rules);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    setIsSubmitting(true);

    try {
      await donorRequestAPI.create({
        request_type: 'corporate_donor',
        ...formData
      });
      toast.success('Your request has been submitted successfully! Our team will reach out to you soon.');
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        organization_name: "",
        organization_type: "",
        message: ""
      });
      setShowRequestForm(false);
    } catch (error) {
      toast.error(error.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    "Enhanced brand visibility and recognition",
    "Access to a network of 500+ professionals",
    "Corporate social responsibility impact",
    "Employee engagement opportunities",
    "Tax benefits and recognition",
    "Partnership opportunities for growth"
  ];

  return (
    <div className="w-full bg-white">
      <HeroSection
        title="Be A Corporate Donor"
        subtitle="Partner with us to create meaningful impact while achieving your corporate social responsibility goals."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        heroImage={groupEvent}
      />

      {/* Introduction */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                Corporate Partnership Opportunities
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Join forward-thinking companies that support our mission through corporate sponsorships,
                matching gifts, and strategic partnerships. Together, we can create lasting positive change.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Benefits */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Benefits of Corporate Partnership
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div className="card">
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="text-primary-600 text-xl flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Request Form Section */}
      {showRequestForm && (
        <section className="section bg-white">
          <div className="section-container">
            <AnimatedSection>
              <div className="max-w-2xl mx-auto">
                <div className="card">
                  <h3
                    className="text-2xl font-bold mb-6"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1F2937'
                    }}
                  >
                    Request to Be a Corporate Donor
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600 ${errors.full_name ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600 ${errors.email ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600 ${errors.phone ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Organization Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="organization_name"
                        value={formData.organization_name}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600 ${errors.organization_name ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {errors.organization_name && <p className="text-red-500 text-xs mt-1">{errors.organization_name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Organization Type
                      </label>
                      <select
                        name="organization_type"
                        value={formData.organization_type}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600"
                      >
                        <option value="">Select organization type...</option>
                        <option value="Corporation">Corporation</option>
                        <option value="Non-Profit">Non-Profit</option>
                        <option value="Foundation">Foundation</option>
                        <option value="Government">Government</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message (Optional)
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600"
                        placeholder="Tell us about your organization and partnership interests..."
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 font-semibold"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Request'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowRequestForm(false)}
                        className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 font-semibold"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section
        className="section text-center"
        style={{
          backgroundColor: '#F9FAFB'
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Partner With Us
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto mb-8"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 400,
                color: '#374151',
                lineHeight: 1.6
              }}
            >
              Submit a request to become a corporate donor and our team will reach out to discuss partnership opportunities.
            </p>
            <button
              onClick={() => setShowRequestForm(true)}
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 600,
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1E3A8A';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1E40AF';
              }}
            >
              Request to Be a Corporate Donor
              <FaArrowRight />
            </button>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
